import React, { useLayoutEffect, useRef } from "react";

const COLOR = "#15AD93";
const GLOW = "21, 173, 147";
const CYCLE = 2800;

// Logo geometry in the 205×205 source image units.
const VIEW = { x: 8, y: 24, w: 190, h: 139 };
const BODY = { x: 96, y: 24, w: 102, h: 139 };
const FOLD = { x: 169, y: 25, w: 28, h: 28 };
// Squares listed in entry order.
const SQUARES = [
  { x: 33, y: 33, w: 22, h: 22 },
  { x: 59, y: 33, w: 21, h: 22 },
  { x: 8, y: 57, w: 21, h: 21 },
  { x: 32, y: 82, w: 22, h: 20 },
  { x: 59, y: 106, w: 21, h: 21 },
  { x: 9, y: 131, w: 22, h: 22 },
  { x: 34, y: 131, w: 22, h: 22 },
];

const EASE_OUT = "cubic-bezier(0.33, 1, 0.68, 1)";
const EASE_OUT_QUINT = "cubic-bezier(0.22, 1, 0.36, 1)";
const EASE_IN_OUT = "cubic-bezier(0.65, 0, 0.35, 1)";

const at = (ms: number) => ms / CYCLE;

const place = (r: { x: number; y: number; w: number; h: number }) => ({
  position: "absolute" as const,
  left: `${((r.x - VIEW.x) / VIEW.w) * 100}%`,
  top: `${((r.y - VIEW.y) / VIEW.h) * 100}%`,
  width: `${(r.w / VIEW.w) * 100}%`,
  height: `${(r.h / VIEW.h) * 100}%`,
  opacity: 0,
});

/** Entry keyframes: hidden until `start`, then eases into place over `duration`. */
const entry = (from: string, start: number, duration: number): Keyframe[] => [
  { offset: 0, opacity: 0, transform: from },
  { offset: at(start), opacity: 0, transform: from, easing: EASE_OUT_QUINT },
  { offset: at(start + duration), opacity: 1, transform: "none" },
  { offset: 1, opacity: 1, transform: "none" },
];

const glow = (alpha: number, blur: number) =>
  `drop-shadow(0 0 ${blur}px rgba(${GLOW}, ${alpha}))`;

// Instances share one cycle start, so a loader replaced by another one
// keeps its place in the animation instead of restarting the entrance.
let mountedCount = 0;
let lastUnmountTime = -Infinity;
let cycleStart = 0;

const timelineNow = () => Number(document.timeline.currentTime ?? 0);

const syncToSharedCycle = (animations: Animation[]) => {
  const now = timelineNow();
  if (mountedCount === 0 && now - lastUnmountTime > 250) cycleStart = now;
  mountedCount += 1;
  animations.forEach((animation) => (animation.startTime = cycleStart));

  return () => {
    mountedCount -= 1;
    lastUnmountTime = timelineNow();
    animations.forEach((animation) => animation.cancel());
  };
};

const SmartdocLoading: React.FC = () => {
  const logoRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const logo = logoRef.current;
    if (!logo) return;

    const pieces = Array.from(logo.children) as HTMLElement[];
    const [body, fold, ...squares] = pieces;
    const loop = { duration: CYCLE, iterations: Infinity };
    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    // Without the Web Animations API the logo is shown still.
    if (typeof logo.animate !== "function") {
      pieces.forEach((el) => (el.style.opacity = "1"));
      return;
    }

    if (reduceMotion) {
      pieces.forEach((el) => (el.style.opacity = "1"));
      const pulse = logo.animate(
        [
          { filter: glow(0, 0) },
          { filter: glow(0.55, 10), easing: EASE_IN_OUT },
          { filter: glow(0, 0) },
        ],
        loop,
      );
      return syncToSharedCycle([pulse]);
    }

    const animations = [
      body.animate(
        [
          { offset: 0, opacity: 1, clipPath: "inset(100% 0 0 0)", easing: EASE_OUT },
          { offset: at(500), opacity: 1, clipPath: "inset(0% 0 0 0)" },
          { offset: 1, opacity: 1, clipPath: "inset(0% 0 0 0)" },
        ],
        loop,
      ),
      fold.animate(entry("translate(43%, -43%)", 420, 350), loop),
      ...squares.map((square, i) =>
        square.animate(
          entry(`translateX(${(-100 / SQUARES[i].w) * 100}%)`, 100 + i * 55, 450),
          loop,
        ),
      ),
      logo.animate(
        [
          { offset: 0, opacity: 1, transform: "none", filter: glow(0, 0) },
          { offset: at(900), opacity: 1, transform: "none", filter: glow(0, 0), easing: EASE_IN_OUT },
          { offset: at(1500), opacity: 1, transform: "none", filter: glow(0.6, 12), easing: EASE_IN_OUT },
          { offset: at(2300), opacity: 1, transform: "none", filter: glow(0, 0), easing: EASE_IN_OUT },
          { offset: at(2600), opacity: 0, transform: "translateY(-12%)", filter: glow(0, 0) },
          { offset: 1, opacity: 0, transform: "translateY(-12%)", filter: glow(0, 0) },
        ],
        loop,
      ),
    ];

    return syncToSharedCycle(animations);
  }, []);

  return (
    <div
      role="status"
      aria-label="Cargando"
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <div
        ref={logoRef}
        style={{
          position: "relative",
          width: 88,
          height: (88 * VIEW.h) / VIEW.w,
        }}
      >
        <svg
          style={place(BODY)}
          viewBox={`0 0 ${BODY.w} ${BODY.h}`}
          aria-hidden="true"
        >
          <path
            fill={COLOR}
            fillRule="evenodd"
            d="M0 0H67V34H102V139H0V126H15V111H0V101H15V86H0V51H16V36H0Z M21 61H38V76H21Z M21 110H37V126H21Z"
          />
        </svg>
        <svg
          style={place(FOLD)}
          viewBox={`0 0 ${FOLD.w} ${FOLD.h}`}
          aria-hidden="true"
        >
          <path fill={COLOR} d="M0 0L28 28H0Z" />
        </svg>
        {SQUARES.map((square, i) => (
          <div key={i} style={{ ...place(square), background: COLOR }} />
        ))}
      </div>
    </div>
  );
};

export default SmartdocLoading;
